import React, { useEffect, useState, useCallback, useRef } from "react";
import { Loading } from "components/ui";
import ErrorView from "components/ui/ErrorView/ErrorView";
import { apiRoute } from "services";
import API from "services/lib/api";
import { dataQueryStatus } from "utils";
import { getErrorMessage, isRequestCanceled } from "utils/helper";
import {
    appMessageUserTypes,
    messageTypes,
} from "../../ChatModule/LiveChat/MessageBody/Messages/Message/enums";
import TMTagDetailElement from "./TMTSuggestionElement/TMTagDetail/TMTagDetailElement/TMTagDetailElement";
import TMTSuggestionElement from "./TMTSuggestionElement/TMTSuggestionElement";
import { RECEIVE_MESSAGE, SUBSCRIBE_TO_TICKET } from "services/socket/events";
import { useSocket } from "services/socket/hook";

const { IDLE, LOADING, ERROR, NULLMODE, DATAMODE } = dataQueryStatus;
const { THIRD_USER } = appMessageUserTypes;
const { DEFAULT } = messageTypes;

const TMessageTagSuggestion = ({
    selectedTicket,
    recentCustomerMessage,
    setCannedMessage,
}) => {
    const { ticketId } = selectedTicket;
    const socket = useSocket() // Access the socket from context
    const [status, setStatus] = useState(LOADING);
    const [errorMssg, setErrorMssg] = useState("");
    const [availableMTs, setAvailableMts] = useState();
    const requestControllerRef = useRef(null);
    const { messageContent } = recentCustomerMessage || {};

    const getMessagingTags = async (httpRequest) => {
        try {
            setStatus(LOADING);
            const res = await API.get(apiRoute.getMessageTags, {
                signal: httpRequest?.signal,
            });

            if (res.status === 200) {
                const { data } = res.data;
                if (data.length > 0) {
                    setAvailableMts(data);
                    setStatus(DATAMODE);
                } else {
                    setStatus(NULLMODE);
                }
            }
        } catch (err) {
            if (isRequestCanceled(err, httpRequest)) {
                return;
            }
            setErrorMssg(getErrorMessage(err));
            setStatus(ERROR);
        }
    };

    const investigateMessageTags = async (messageContent, httpRequest) => {
        try {
            // Check if messageContent exists and is not empty after trimming
            const trimmedContent = messageContent?.trim();
            if (trimmedContent && trimmedContent.length > 0) {
                setStatus(LOADING);
                setErrorMssg();

                const url = apiRoute?.investigateMessage;
                const res = await API.get(url, {
                    params: {
                        search: trimmedContent,
                    },
                    signal: httpRequest?.signal,
                });
                if (res.status === 200) {
                    const { data } = res.data;
                    if (data.length > 0) {
                        setAvailableMts(data);
                        setStatus(DATAMODE);
                    } else {
                        setStatus(NULLMODE);
                    }
                }
            } else {
                // If message is empty, just get regular messaging tags
                getMessagingTags(httpRequest);
            }
        } catch (err) {
            if (isRequestCanceled(err, httpRequest)) {
                return;
            }
            setStatus(ERROR);
            setErrorMssg(getErrorMessage(err));
        }
    };

    const startMessageTagRequest = (content = messageContent) => {
        requestControllerRef.current?.abort();
        const httpRequest = new AbortController();
        requestControllerRef.current = httpRequest;

        if (content?.trim()) {
            investigateMessageTags(content, httpRequest);
        } else {
            getMessagingTags(httpRequest);
        }
    };

    const renderBasedOnStatus = () => {
        switch (status) {
            case IDLE:
                return "";

            case LOADING:
                return <Loading />;

            case NULLMODE:
                return (
                    <>
                        <TMTagDetailElement
                            lastMessageContent={messageContent}
                            selectedTicket={selectedTicket}
                        />
                    </>
                );

            case DATAMODE:
                return (
                    <TMTSuggestionElement
                        selectedTicket={selectedTicket}
                        availableMTs={availableMTs}
                        lastMessageContent={messageContent}
                        setCannedMessage={setCannedMessage}
                    />
                );

            case ERROR:
                return (
                    <ErrorView
                        message={errorMssg}
                        handleRetry={startMessageTagRequest}
                    />
                );

            default:
                return "";
        }
    };

    const acceptNewMessageForInvestigation = useCallback((message) => {
        const { messageType, senderType, messageContent } = message;
        // Only investigate if message type is DEFAULT, sender is THIRD_USER, and message is not empty
        const trimmedContent = messageContent?.trim();
        if (messageType === DEFAULT && senderType === THIRD_USER && trimmedContent && trimmedContent.length > 0) {
            startMessageTagRequest(messageContent);
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        startMessageTagRequest(messageContent);
        
        socket?.emit(SUBSCRIBE_TO_TICKET, { ticketId });
        socket?.on(RECEIVE_MESSAGE, acceptNewMessageForInvestigation);

        return () => {
            requestControllerRef.current?.abort();
            requestControllerRef.current = null;
            socket?.off(RECEIVE_MESSAGE, acceptNewMessageForInvestigation);
        };
        //eslint-disable-next-line
    }, []);

    return <div>{renderBasedOnStatus()}</div>;
};

export default TMessageTagSuggestion;
